import { lazy, Suspense, useEffect } from "react";
import {
  matchRoutes,
  navigate,
  RouteObject,
  useLocation,
  useRoutes,
} from "../../helpers/tixxt-router";
import Loading from "../shared/Loading";
import serverAccessDenied from "../shared/errors/serverAccessDenied";
import { getRoutes } from "../../routes";
import invariant from "invariant";
import { memoize } from "lodash";
import {
  allowNavigation,
  getConfirmationMessage,
} from "../../hooks/useInterceptorPrompt";
import { isBuildStale } from "../../helpers/staleBuild";

let spaRoot: HTMLElement | null;

const AccessDenied = lazy(() => import("../shared/errors/AccessDenied"));

export default function TixxtContent() {
  const element = useRoutes(getRoutes());
  const { pathname } = useLocation();
  const accessDenied = element ? serverAccessDenied(pathname) : null;

  useEffect(() => {
    if (element) toggleFrontend("spa", { source: "routes" });
  }, [element]);

  return (
    <>
      {accessDenied ? (
        <Suspense fallback={<Loading />}>
          <AccessDenied joinableGroups={accessDenied.resolve_via?.group_join} />
        </Suspense>
      ) : (
        element
      )}
      <TurboCompat routes={getRoutes()} />
    </>
  );
}

function TurboCompat({ routes }: { routes: RouteObject[] }) {
  useEffect(() => {
    function spaNavigateInstead(event) {
      // Cancel visit if confirmation is required but not given
      const confirmationMessage = getConfirmationMessage();
      if (confirmationMessage && !confirm(confirmationMessage)) {
        event.preventDefault();
        return;
      }
      // navigate() leaves the page while the build is stale, do not ask twice
      if (confirmationMessage && isBuildStale()) allowNavigation();

      // Turn turbo:visit into spa navigate
      const url = new URL(event.detail.url);
      const match = matchRoutes(routes, url.pathname);
      if (match && !match.forceTurbo) {
        event.preventDefault();
        navigate(url);
      }
    }

    window.addEventListener("turbo:before-visit", spaNavigateInstead);
    return () => {
      window.removeEventListener("turbo:before-visit", spaNavigateInstead);
    };
  }, [routes]);
  return null;
}

function toggleFrontend(type: "spa" | "turbo", meta?: { source?: string }) {
  if (!spaRoot) {
    spaRoot = document.getElementById("spa-root");
    invariant(spaRoot, "spa-root missing");
  }

  if (type == "spa") {
    if (spaRoot.classList.contains("hidden")) {
      window.debug(`⚛️ toggleFrontend spa (source=${meta?.source})`);
      spaRoot.classList.toggle("hidden", false);
    }
    document.getElementById("turbo-root")?.classList.toggle("hidden", true);
  } else if (type == "turbo") {
    if (!spaRoot.classList.contains("hidden")) {
      window.debug(`⚡️ toggleFrontend turbo (source=${meta?.source})`);
      spaRoot.classList.toggle("hidden", true);
    }
    document.getElementById("turbo-root")?.classList.toggle("hidden", false);
  }
}

export const frontendByLocation = memoize(function (path: string) {
  if (matchRoutes(getRoutes(), path)) {
    return "spa";
  } else {
    return "turbo";
  }
});

window.addEventListener("popstate", () => {
  toggleFrontend(frontendByLocation(location.pathname), { source: "popstate" });
});
window.addEventListener("turbo:render", () => {
  toggleFrontend(frontendByLocation(location.pathname), {
    source: "turbo:render",
  });
});

function nativeLocaleLinks(event) {
  // prevent turbo from handling this link click
  const url = new URL(event["detail"].url);
  if (url.searchParams.get("locale")) event.preventDefault();
}

window.addEventListener("turbo:click", nativeLocaleLinks);
